import {
  EstimateSchema,
  EstimateStatus,
  ZodCreateEstimateSchema,
  ZodEditEstimateSchema,
  ZodGetEstimatesParams,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Estimate = EstimateSchema;
export type EstimateImageType = "logo" | "signature";

export const getEstimate = async (estimateId: string): Promise<Estimate> => {
  const response = await apiClient.get<{ estimate: Estimate }>(`/api/estimate/${estimateId}`);
  return response.data.estimate;
};

/**
 * Get estimates - returns all estimates (for backward compatibility)
 * @deprecated Use getEstimatesPaginated for better performance with large datasets
 */
export const getEstimates = async (params?: ZodGetEstimatesParams): Promise<Estimate[]> => {
  const response = await apiClient.get<{ estimates: Estimate[]; pagination: PaginationMeta }>("/api/estimate", { params });
  return response.data.estimates;
};

/**
 * Paginated response for estimates
 */
export interface GetEstimatesPaginatedResponse {
  estimates: Estimate[];
  pagination: PaginationMeta;
}

/**
 * Get estimates with pagination support
 */
export const getEstimatesPaginated = async (
  params?: ZodGetEstimatesParams & PaginationParams,
): Promise<GetEstimatesPaginatedResponse> => {
  const response = await apiClient.get<GetEstimatesPaginatedResponse>("/api/estimate", { params });
  return response.data;
};

export const createEstimate = async (
  estimateData: ZodCreateEstimateSchema,
): Promise<{ success: boolean; message: string; estimateId: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string; estimateId: string }>(
    "/api/estimate",
    estimateData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateEstimate = async (
  estimateData: ZodEditEstimateSchema,
): Promise<{ success: boolean; message: string; estimate: Estimate }> => {
  const response = await apiClient.put<{ success: boolean; message: string; estimate: Estimate }>(
    `/api/estimate/${estimateData.id}`,
    estimateData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateEstimateStatus = async (
  estimateId: string,
  status: EstimateStatus,
): Promise<{ success: boolean; message: string; estimate: Estimate }> => {
  const response = await apiClient.patch<{ success: boolean; message: string; estimate: Estimate }>(
    `/api/estimate/${estimateId}/status`,
    { status },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const deleteEstimate = async (estimateId: string): Promise<{ message: string; estimate: Estimate }> => {
  const response = await apiClient.delete<{ message: string; estimate: Estimate }>(`/api/estimate/${estimateId}`);
  return response.data;
};

export const generateEstimateNumber = async (relationId: string): Promise<{ estimateNumber: string }> => {
  const response = await apiClient.post<{ estimateNumber: string }>(
    "/api/estimate/generate-number",
    { relationId },
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
};

export interface SaveAndSendEstimateData {
  id?: string;
  estimateData: ZodCreateEstimateSchema;
  emailData: {
    to: string;
    cc?: string | null;
    bcc?: string | null;
    subject: string;
    content: string;
    pdfContent: string;
  };
}

export const saveAndSendEstimate = async (data: SaveAndSendEstimateData): Promise<{ message: string }> => {
  const response = await apiClient.post<{ message: string }>("/api/estimate/save-and-send", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Fetch all estimates by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllEstimates = async (params?: ZodGetEstimatesParams): Promise<Estimate[]> => {
  const allEstimates: Estimate[] = [];
  let cursor: string | null = null;

  do {
    const response = await getEstimatesPaginated({ ...params, cursor, limit: 100 });
    allEstimates.push(...response.estimates);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allEstimates;
};

export default getEstimate;
